/**
 * A small in-memory query cache — the TanStack Query / SWR model, kept to what
 * slowly-changing admin data needs:
 *
 *  - fresh hits return instantly: a tab switch or remount re-reads the cache,
 *    not the network (staleMs);
 *  - concurrent identical reads share ONE request (two components mounting,
 *    or React StrictMode's double effect in dev);
 *  - mutations patch entries in place (`update`) or drop them (`invalidate`),
 *    so the UI reflects a change without re-downloading lists;
 *  - `force` bypasses freshness — the manual refresh button.
 *
 * Scoped to the signed-in user + tenant (read from the access token): a
 * different login in the same tab — or a logout — starts from empty, so one
 * account can never be shown another's cached data.
 *
 * Race safety: every write to a key bumps its version, and a response is only
 * stored if the key's version is unchanged since its request started — a slow
 * pre-mutation response can't overwrite a newer patch.
 */

interface Entry<T> {
  data?: T;
  at: number; // when `data` was fetched (ms)
  version: number;
  inflight?: Promise<T>;
}

function currentScope(): string {
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    return `${payload.sub ?? ''}:${payload.tenantId ?? ''}`;
  } catch {
    return '';
  }
}

export function createQueryCache(staleMs: number) {
  const entries = new Map<string, Entry<unknown>>();
  let scope = '';
  // Global, never reused: a key deleted by invalidate() and re-requested can't
  // collide with the version of a response still in flight from before.
  let seq = 0;

  const ensureScope = () => {
    const s = currentScope();
    if (s !== scope) {
      entries.clear();
      scope = s;
    }
  };

  const bump = (key: string): Entry<unknown> => {
    const e = entries.get(key) ?? { at: 0, version: 0 };
    const next = { data: e.data, at: e.at, version: ++seq }; // drops any inflight
    entries.set(key, next);
    return next;
  };

  return {
    /** Cached value if fresh, else fetch (sharing any identical in-flight request). */
    get<T>(key: string, fetcher: () => Promise<T>, opts: { force?: boolean } = {}): Promise<T> {
      ensureScope();
      const e = entries.get(key) as Entry<T> | undefined;
      if (!opts.force) {
        if (e?.data !== undefined && Date.now() - e.at < staleMs) return Promise.resolve(e.data);
        if (e?.inflight) return e.inflight;
      }
      const version = ++seq;
      const p: Promise<T> = fetcher().then(
        (data) => {
          if (entries.get(key)?.version === version) entries.set(key, { data, at: Date.now(), version });
          return data;
        },
        (err) => {
          const cur = entries.get(key);
          if (cur?.version === version) entries.set(key, { data: cur.data, at: cur.at, version });
          throw err;
        }
      );
      entries.set(key, { data: e?.data, at: e?.at ?? 0, version, inflight: p as Promise<unknown> });
      return p;
    },

    /** The cached value if present and fresh — synchronous, never fetches. */
    peek<T>(key: string): T | undefined {
      ensureScope();
      const e = entries.get(key) as Entry<T> | undefined;
      return e?.data !== undefined && Date.now() - e.at < staleMs ? e.data : undefined;
    },

    /** Patch every cached entry whose key starts with `prefix` (keeps freshness). */
    update<T>(prefix: string, fn: (data: T) => T): void {
      ensureScope();
      for (const key of [...entries.keys()]) {
        if (!key.startsWith(prefix)) continue;
        const e = entries.get(key)!;
        if (e.data === undefined) continue;
        const next = bump(key);
        next.data = fn(e.data as T);
        next.at = e.at;
      }
    },

    /** Drop entries (all, or those whose key starts with `prefix`) — next read refetches. */
    invalidate(prefix = ''): void {
      for (const key of [...entries.keys()]) {
        if (key.startsWith(prefix)) {
          bump(key);
          entries.delete(key);
        }
      }
    },
  };
}
